import { isRecord, nullableString, request, requireArray, requireNumber, requireString } from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

export type AlertRecipientCandidate = components['schemas']['AlertRecipientCandidate']

export function findAlertRecipients(entityId: string, kind: 'user' | 'group', search: string, signal: AbortSignal): Promise<AlertRecipientCandidate[]> {
  const query = new URLSearchParams({ kind, search })
  return request(`/api/v1/entities/${entityId}/alert-rules/recipient-candidates?${query}`, (raw) => requireArray(raw, 'recipient candidates', (item) => {
    if (!isRecord(item) || item.type !== kind) throw new Error('Malformed recipient candidate')
    return { type: kind, id: requireString(item.id, 'candidate id'), name: requireString(item.name, 'candidate name'),
      email: nullableString(item.email, 'candidate email') } satisfies AlertRecipientCandidate
  }), { signal })
}

export type AlertFieldType = 'string' | 'double' | 'int'
export type AlertCatalogField = {
  id: string; label: string; type: AlertFieldType; unit: string; operators: string[]; events: string[]; allowedValues: string[]
}
export type AlertCatalog = {
  schemaVersion: number; catalogVersion: string; evaluatorVersion: string; assetTypes: string[]; events: string[]
  predicateLanguages: string[]; booleanOperators: string[]; fields: AlertCatalogField[]; builderDepth: number | null
}

function requireStrings(raw: unknown, name: string): string[] {
  return requireArray(raw, name, (item) => requireString(item, name))
}

function decodeField(raw: unknown): AlertCatalogField | null {
  if (!isRecord(raw)) throw new Error('Malformed alert field')
  const type = requireString(raw.type, 'field type')
  if (type !== 'string' && type !== 'double' && type !== 'int') return null
  return {
    id: requireString(raw.id, 'field id'), label: requireString(raw.label, 'field label'), type,
    unit: typeof raw.unit === 'string' ? raw.unit : '',
    operators: requireStrings(raw.operators, 'field operators'), events: requireStrings(raw.events, 'field events'),
    allowedValues: raw.allowedValues == null ? [] : requireStrings(raw.allowedValues, 'field allowed values'),
  }
}

function decodeCatalog(raw: unknown): AlertCatalog {
  if (!isRecord(raw)) throw new Error('Malformed alert capabilities')
  const limits = isRecord(raw.limits) ? raw.limits : {}
  return {
    schemaVersion: requireNumber(raw.schemaVersion, 'schemaVersion'),
    catalogVersion: requireString(raw.catalogVersion, 'catalogVersion'),
    evaluatorVersion: requireString(raw.evaluatorVersion, 'evaluatorVersion'),
    assetTypes: requireStrings(raw.assetTypes, 'assetTypes'), events: requireStrings(raw.events, 'events'),
    predicateLanguages: requireStrings(raw.predicateLanguages, 'predicateLanguages'),
    booleanOperators: requireStrings(raw.booleanOperators, 'booleanOperators'),
    fields: requireArray(raw.fields, 'fields', decodeField).filter((field) => field !== null),
    builderDepth: typeof limits.builderDepth === 'number' ? limits.builderDepth : null,
  }
}

export function getAlertCapabilities(entityId: string, signal?: AbortSignal): Promise<AlertCatalog> {
  return request(`/api/v1/entities/${entityId}/alert-capabilities`, decodeCatalog, { signal })
}
